import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import {
  Botao,
  CheckboxUnform,
  DataInputUnform,
  FormRef,
  FormUnform,
  ModalBase,
  SelectOpcao,
  SelectUnform,
  TextAreaUnform
} from 'src/componentes'
import { Api } from 'src/servicos'
import {
  ProtocoloRescisaoDetalheDto,
  AtualizarDadosGeraisRescisao as AtualizarDadosGeraisRescisaoDados
} from 'src/servicos/api/rescisao'
import {
  SetorRescisao,
  SetorRescisaoPorExtenso,
  StatusRescisao,
  StatusRescisaoPorExtenso
} from 'src/tipos'

import { AcoesModal, CorpoModal, TituloModal } from './styles'

export interface ModalEditarGeraisRescisaoRef {
  abrir: (protocolo: ProtocoloRescisaoDetalheDto) => void
  fechar: () => void
}

interface ModalEditarGeraisRescisaoProps {
  backdrop?: boolean
  aoSalvar: () => void
}

const TEXTO_MOTIVO_OUTRO = 'Outro'

// Reversão, AguardandoRespostaFormulario e AguardandoTermoRescisao têm endpoint/botão dedicado
// (Reverter rescisão / Enviar formulário / Enviar termo de rescisão, cada um dispara seu próprio
// e-mail) — nenhum dos três pode ser ESCOLHIDO manualmente aqui.
const STATUS_SOMENTE_BOTAO_DEDICADO = [
  StatusRescisao.Reversao,
  StatusRescisao.AguardandoRespostaFormulario,
  StatusRescisao.AguardandoTermoRescisao
]

const opcoesStatusBase = Object.keys(StatusRescisao)
  .filter(chave => !STATUS_SOMENTE_BOTAO_DEDICADO.includes(chave as StatusRescisao))
  .map(chave => ({
    id: chave,
    texto: StatusRescisaoPorExtenso[chave as keyof typeof StatusRescisao]
  }))

// Se o protocolo já está em um desses status (aberto pelo botão dedicado), ele precisa continuar
// aparecendo pré-selecionado no campo — senão o select fica vazio ao abrir o modal só para editar
// outro campo (ex.: Setor, Caso especial), sem intenção de mudar o status. A opção some de novo
// assim que outro status é escolhido, então não fica disponível para reselecionar manualmente.
const obterOpcoesStatus = (statusAtual?: string): SelectOpcao[] =>
  statusAtual &&
  STATUS_SOMENTE_BOTAO_DEDICADO.includes(statusAtual as StatusRescisao)
    ? [
        {
          id: statusAtual,
          texto:
            StatusRescisaoPorExtenso[
              statusAtual as keyof typeof StatusRescisao
            ] ?? statusAtual
        },
        ...opcoesStatusBase
      ]
    : opcoesStatusBase

const ModalEditarGeraisRescisaoComponente: React.ForwardRefRenderFunction<
  ModalEditarGeraisRescisaoRef,
  ModalEditarGeraisRescisaoProps
> = ({ backdrop, aoSalvar }, ref) => {
  const formRef = useRef<FormRef>(null)
  const [aberta, definirAberta] = useState(false)
  const [carregando, definirCarregando] = useState(false)
  const [protocoloId, definirProtocoloId] = useState<string>(null)
  const [dadosIniciais, definirDadosIniciais] = useState<
    AtualizarDadosGeraisRescisaoDados
  >(null)
  const [motivos, definirMotivos] = useState<SelectOpcao[]>([])
  const [opcoesStatus, definirOpcoesStatus] = useState<SelectOpcao[]>(
    opcoesStatusBase
  )
  const [motivoOutroSelecionado, definirMotivoOutroSelecionado] = useState(
    false
  )

  useEffect(() => {
    const carregar = async () => {
      const opcoes = await Api.ListarMotivosRescisaoOpcoes()
      definirMotivos(opcoes)
    }

    carregar()
  }, [])

  const fechar = () => {
    definirAberta(false)
    definirProtocoloId(null)
    definirDadosIniciais(null)
    definirCarregando(false)
  }

  const abrir = (protocolo: ProtocoloRescisaoDetalheDto) => {
    definirProtocoloId(protocolo.id)
    definirOpcoesStatus(obterOpcoesStatus(protocolo.status))
    definirDadosIniciais({
      status: protocolo.status,
      setor: protocolo.setorResponsavel,
      motivoId: protocolo.motivoId ?? null,
      motivoDetalhado: protocolo.motivoDetalhado,
      motivoOutroTexto: protocolo.motivoOutroTexto,
      casoEspecial: protocolo.casoEspecial,
      dataPrimeiroContato: protocolo.dataPrimeiroContato ?? null
    })
    definirMotivoOutroSelecionado(protocolo.motivo === TEXTO_MOTIVO_OUTRO)
    definirAberta(true)
  }

  useImperativeHandle(ref, () => ({ abrir, fechar }))

  const aoAlterarMotivo = (opcao: SelectOpcao) => {
    definirMotivoOutroSelecionado(opcao?.texto === TEXTO_MOTIVO_OUTRO)
  }

  const salvar = async (dados: AtualizarDadosGeraisRescisaoDados) => {
    if (!protocoloId) return

    if (motivoOutroSelecionado && !dados.motivoOutroTexto) {
      formRef.current?.setErrors({
        motivoOutroTexto: 'Este campo deve ser preenchido!'
      })
      return
    }

    try {
      definirCarregando(true)
      await Api.AtualizarDadosGeraisRescisao(protocoloId, dados)
      aoSalvar()
      fechar()
    } finally {
      definirCarregando(false)
    }
  }

  return (
    <ModalBase id="modal-editar-gerais-rescisao" aberta={aberta} backdrop={backdrop}>
      <CorpoModal>
        <TituloModal>Editar dados gerais do protocolo</TituloModal>
        {dadosIniciais && (
          <FormUnform
            ref={formRef}
            dadosIniciais={dadosIniciais}
            acaoSucesso={salvar}
          >
            <SelectUnform
              id="slct_status_gerais"
              name="status"
              label="Status"
              opcoes={opcoesStatus}
              obrigatorio
            />
            <SelectUnform
              id="slct_setor_gerais"
              name="setor"
              label="Setor"
              iconeFechar
              opcoes={[
                {
                  id: SetorRescisao.Academico,
                  texto: SetorRescisaoPorExtenso.Academico
                },
                {
                  id: SetorRescisao.Financeiro,
                  texto: SetorRescisaoPorExtenso.Financeiro
                }
              ]}
            />
            <SelectUnform
              id="slct_motivo_gerais"
              name="motivoId"
              label="Motivo"
              iconeFechar
              opcoes={motivos}
              valorAlterado={aoAlterarMotivo}
            />
            {motivoOutroSelecionado && (
              <TextAreaUnform
                id="txt_motivo_outro_gerais"
                name="motivoOutroTexto"
                label="Motivo customizado"
                maxLength={500}
                obrigatorio
              />
            )}
            <TextAreaUnform
              id="txt_motivo_detalhado_gerais"
              name="motivoDetalhado"
              label="Motivo detalhado"
              maxLength={2000}
            />
            <DataInputUnform
              id="dt_primeiro_contato_gerais"
              name="dataPrimeiroContato"
              label="Data do 1º contato"
            />
            <CheckboxUnform
              id="chk_caso_especial_gerais"
              name="casoEspecial"
              texto="Caso especial"
            />
            <AcoesModal>
              <Botao
                texto="Cancelar"
                tema="Secundario"
                type="button"
                disabled={carregando}
                onClick={fechar}
              />
              <Botao texto="Salvar" type="submit" carregando={carregando} />
            </AcoesModal>
          </FormUnform>
        )}
      </CorpoModal>
    </ModalBase>
  )
}

export const ModalEditarGeraisRescisao = forwardRef(
  ModalEditarGeraisRescisaoComponente
)
